// Before the first table loads (design/07-ui-shell.md §3): a statement, not a
// tour, so an almost-empty screen reads as deliberate. Once per account,
// dismissible (plan §8). It lies over the arrival page for a person who has
// a seat here, and the shell does not render until it is dismissed. And the
// offer to switch presets — offered, never automatic, and the offer can be
// turned off (§7).
import { useEffect, useRef, useState } from 'react';
import type { Preset } from '@dragons-table/vtt-shared';

export function Onboarding({ onDone }: { onDone: () => void }) {
  const button = useRef<HTMLButtonElement>(null);
  useEffect(() => button.current?.focus(), []);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-stone-950/95 p-6">
      <section role="dialog" aria-modal="true" aria-labelledby="onboarding-title" data-testid="onboarding"
        className="max-w-lg w-full rounded-lg border border-stone-800 bg-stone-900 p-6 space-y-4 text-stone-100">
        <h1 id="onboarding-title" className="text-2xl font-semibold">Your table is yours to arrange</h1>
        <p className="text-stone-300">
          A new table looks almost empty on purpose: three bars, one thing each, and everything else behind the menus on the top bar.
        </p>
        <p className="text-stone-300">
          Every bar carries a <span className="font-mono">+</span>. Put on a bar what you use constantly, keep what you use often in a drop-down you name,
          and leave the rest in the menus — nothing is ever lost by removing it, because the top bar always holds everything.
        </p>
        <p className="text-stone-300">
          Save arrangements as presets, switch between them when the work changes, and open a second window for a second screen.
          Your arrangement is yours: nobody at the table can change it.
        </p>
        <div className="flex justify-end">
          <button ref={button} type="button" className="vtt-btn-primary rounded bg-amber-600 px-4 py-1.5 font-medium text-stone-950 hover:bg-amber-500" onClick={onDone}>
            Got it
          </button>
        </div>
      </section>
    </div>
  );
}

export function OfferBanner({ preset, back = false, onSwitch, onDismiss }: { preset: Preset; back?: boolean; onSwitch: () => void; onDismiss: () => void }) {
  // A fight over (qa-runs#404): the layout from before it, by its preset's name.
  const what = back ? (preset.name === 'previous' ? 'Switch back to your previous layout?' : `Switch back to your "${preset.name}" layout?`) : `Switch to your "${preset.name}" layout?`;
  return (
    <div role="status" data-testid="offer" className="flex flex-wrap items-center gap-3 border-b border-stone-800 bg-stone-900 px-3 py-1.5 text-sm">
      <span>{what}</span>
      <button type="button" className="vtt-btn-primary rounded bg-amber-600 px-3 py-0.5 font-medium text-stone-950 hover:bg-amber-500" onClick={onSwitch}>Switch</button>
      <button type="button" className="rounded border border-stone-600 px-3 py-0.5 hover:bg-stone-800" onClick={onDismiss}>Not now</button>
    </div>
  );
}

const PHONE_NOTICE_KEY = 'vtt.phoneNotice.dismissed';
const readDismissed = () => { try { return sessionStorage.getItem(PHONE_NOTICE_KEY) === '1'; } catch { return false; } };

/**
 * On a phone (decision #1442, Tom's B): the table needs a tablet or a larger screen, said plainly. A notice above the
 * table rather than a window in front of it: the table under it still works — a player can roll, read the log or
 * follow along — and "Continue anyway" puts it away for the rest of the visit. The first-arrival window is not touched.
 */
export function PhoneNotice({ phone }: { phone: boolean }) {
  const [dismissed, setDismissed] = useState(readDismissed);
  if (!phone || dismissed) return null;
  return (
    <div role="status" data-testid="phone-notice" className="flex flex-wrap items-center gap-2 border-b border-amber-700 bg-stone-900 px-3 py-2 text-sm text-amber-200">
      <span className="min-w-0 flex-1">The table needs a tablet or a larger screen. On a phone the board is too small to play on.</span>
      <button type="button" className="rounded border border-stone-600 px-3 py-0.5 text-stone-100 hover:bg-stone-800"
        onClick={() => { try { sessionStorage.setItem(PHONE_NOTICE_KEY, '1'); } catch { /* the notice still goes */ } setDismissed(true); }}>
        Continue anyway
      </button>
    </div>
  );
}
